import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { usePermission } from '../../../../auth/AuthContext';
import { inventoryCategoriesApi, inventoryItemTypesApi } from '../inventoryApi';
import type { InventoryCategoryOption, InventoryItemTypeListItem } from '../inventoryTypes';
import { sampleInventoryItemType } from '../utils/sampleInventory';

/**
 * Create or edit one item type — the governed name an item is drawn from.
 */
export function InventoryItemTypeModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: InventoryItemTypeListItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const canReadCategories = usePermission('inventory-categories.read');

  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [categoryId, setCategoryId] = useState(editing?.inventoryCategoryId ?? '');

  const [categories, setCategories] = useState<InventoryCategoryOption[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open || !canReadCategories) return;

    const controller = new AbortController();
    setLoadingCategories(true);

    inventoryCategoriesApi
      .options(controller.signal)
      .then(setCategories)
      // A failed picker load is not the form's fault and names no field, so it must not go
      // through the save path's error state — the field simply renders with nothing in it.
      .catch(() => undefined)
      .finally(() => setLoadingCategories(false));

    return () => controller.abort();
  }, [open, canReadCategories]);

  const valid = name.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      name: name.trim(),
      description: description.trim() || null,
      inventoryCategoryId: categoryId === '' ? null : categoryId,
    };

    try {
      if (editing) await inventoryItemTypesApi.update(editing.id, request);
      else await inventoryItemTypesApi.create(request);

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the item type.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'inventory-item-type' });
    } finally {
      setSaving(false);
    }
  }

  /*
    A row already scoped to a since-deleted category keeps reading correctly: the option is
    injected so the field states what the record says rather than showing a placeholder. The
    server agrees — it refuses a fresh write against a deleted category and leaves an existing
    one alone.
  */
  const options = [...categories.map((c) => ({ value: c.id, label: c.name }))];

  if (
    editing?.inventoryCategoryId &&
    editing.inventoryCategoryName &&
    !categories.some((c) => c.id === editing.inventoryCategoryId)
  ) {
    options.unshift({
      value: editing.inventoryCategoryId,
      label: `${editing.inventoryCategoryName} (deleted)`,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit item type' : 'New item type'}
      size="lg"
      id="inventory-item-type-modal"
      autogenerate={{
        onGenerate: () => {
          const sample = sampleInventoryItemType();
          setName(sample.name);
          setDescription(sample.description);
        },
        disabled: saving,
      }}
      confirm={{
        id: 'inventory-item-type-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="inventory-item-type-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="inventory-item-type-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id="inventory-item-type-name"
          label="Name"
          required
          description="The canonical name items are drawn from, e.g. Examination Gloves. Held once among the live rows — that is the whole reason this catalog exists."
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
          disabled={saving}
          error={error?.fieldError('name')}
        />

        <TextField
          id="inventory-item-type-description"
          label="Description"
          description="Optional note for an administrator maintaining the vocabulary."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          disabled={saving}
          error={error?.fieldError('description')}
        />

        {/*
          Gated on the CATEGORY catalog's read key, not this module's: it is another module's
          data. Hidden rather than disabled, and the field is optional, so a reader without that
          grant still gets a complete form — the name simply stays offered everywhere.
        */}
        {canReadCategories ? (
          <Select
            id="inventory-item-type-inventory-category-id"
            label="Offered under"
            description="Leave empty to offer this name under every category — the deliberate default, for a name that genuinely spans them. Set it to narrow the name to one."
            placeholder="Every category"
            value={categoryId}
            onChange={setCategoryId}
            options={options}
            remote
            loading={loadingCategories}
            disabled={saving}
            error={error?.fieldError('inventoryCategoryId')}
          />
        ) : null}
      </div>
    </Modal>
  );
}
